import React, { useState, useMemo } from 'react';
import { ChartListIcon } from '@tanium/react-graphics';
import { useTranslation } from 'react-i18next';
import { PageSection } from '@tanium/react-page-section';
import { Well } from '@tanium/react-well';
import { Stack } from '@tanium/react-stack';
import { Box } from '@tanium/react-box';
import { TaniumSpinner } from '@tanium/react-tanium-spinner';
import { TextWithIcon } from '@tanium/react-text-with-icon';
import { Notification } from '@tanium/react-persistent-notifications';
import { LineChart } from '@tanium/tanviz';
import { useTheme } from '@tanium/react-theme-context';
import { ThemeColors } from '@tanium/theme-data';
import { MetricHistory } from '../../types';
import { AsyncStatus } from '../../hooks/useAsync';
import { useMetricHistory } from '../../hooks/useMetricHistory';
import { getDateFromDateOnlyString } from '../../utilities/dateHelper';

function toLineChartData(history: MetricHistory) {
  const aggregated = {
    dataKey: 'all',
    label: 'All Tanium Instances',
    value: history.aggregated.map(
      (dp) => [getDateFromDateOnlyString(dp.date).getTime(), dp.totalValue] as [number, number]
    ),
  };
  const perInstance = history.instances.map((inst) => ({
    dataKey: inst.instanceId,
    label: inst.friendlyName || inst.instanceName,
    value: inst.data.map((dp) => [getDateFromDateOnlyString(dp.date).getTime(), dp.value] as [number, number]),
  }));
  return { aggregated, perInstance };
}

interface ChartCardProps {
  title: string;
  status: AsyncStatus;
  history: MetricHistory | undefined;
  selectedInstanceId: string;
}

const ChartCard: React.VFC<ChartCardProps> = ({ title, status, history, selectedInstanceId }) => {
  const { t } = useTranslation();

  const isLoading = status === AsyncStatus.Pending || status === AsyncStatus.Idle;
  const isError = status === AsyncStatus.Failure;
  const isEmpty = status === AsyncStatus.Success && (!history || history.aggregated.length === 0);

  const chartData = useMemo(() => {
    if (!history) return null;
    const { aggregated, perInstance } = toLineChartData(history);
    if (selectedInstanceId === 'all') return [aggregated];
    const instanceData = perInstance.find((d) => d.dataKey === selectedInstanceId);
    return instanceData ? [instanceData] : [aggregated];
  }, [history, selectedInstanceId]);

  return (
    <Well bg={ThemeColors.Light0}>
      <PageSection title={t(title)}>
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2} height={280} width="100%">
            <TaniumSpinner />
          </Box>
        )}
        {isError && (
          <Notification role="alert" type="warning">
            {t('Failed to load data')}
          </Notification>
        )}
        {isEmpty && (
          <Notification role="note" type="info">
            {t('No data available')}
          </Notification>
        )}
        {status === AsyncStatus.Success && history && history.aggregated.length > 0 && chartData && (
          <LineChart
            data={chartData}
            width="100%"
            yAxisLabel={t('Endpoints')}
            xTickFormatter={(tick) => new Date(Number(tick)).toLocaleDateString()}
            isAnimated={false}
          />
        )}
      </PageSection>
    </Well>
  );
};

export const MetricsSection: React.VFC = () => {
  const { t } = useTranslation();
  const [selectedInstanceId, setSelectedInstanceId] = useState('all');

  const longLivedResult = useMetricHistory(10, 'long_lived_30d');
  const ephemeralResult = useMetricHistory(10, 'ephemeral_30d');

  const longLivedHistory = longLivedResult.metricHistory;
  const ephemeralHistory = ephemeralResult.metricHistory;
  const longLivedStatus = longLivedResult.loadMetricHistory.status;
  const ephemeralStatus = ephemeralResult.loadMetricHistory.status;

  // Derive instance options from whichever dataset has loaded first.
  const instances = useMemo(() => {
    const history = longLivedHistory ?? ephemeralHistory;
    return history?.instances ?? [];
  }, [longLivedHistory, ephemeralHistory]);

  const theme = useTheme();
  const selectStyle: React.CSSProperties = {
    backgroundColor: theme.colors[ThemeColors.Background],
    color: theme.colors[ThemeColors.InputText],
    border: `1px solid ${theme.colors[ThemeColors.InputBorder]}`,
    borderRadius: 4,
    padding: '4px 28px 4px 8px',
    fontSize: 14,
    lineHeight: '20px',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23646A7E' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '10px 6px',
    minWidth: 180,
  };

  const instanceSelector =
    instances.length > 0 ? (
      <Box display="flex" justifyContent="flex-end" width="100%">
        <select
          value={selectedInstanceId}
          onChange={(e) => setSelectedInstanceId(e.target.value)}
          aria-label={t('Select instance')}
          style={selectStyle}
        >
          <option value="all">{t('All Tanium Instances')}</option>
          {instances.map((inst) => (
            <option key={inst.instanceId} value={inst.instanceId}>
              {inst.friendlyName || inst.instanceName}
            </option>
          ))}
        </select>
      </Box>
    ) : null;

  return (
    <PageSection
      title={<TextWithIcon leadingIcon={ChartListIcon}>{t('Metrics')}</TextWithIcon>}
      additionalTitleContent={instanceSelector}
    >
      <Stack direction="row" itemSpacing={1} width="100%" itemGrow={1}>
        <ChartCard
          title="Long-Lived Endpoints"
          status={longLivedStatus}
          history={longLivedHistory}
          selectedInstanceId={selectedInstanceId}
        />
        <ChartCard
          title="Ephemeral Endpoints"
          status={ephemeralStatus}
          history={ephemeralHistory}
          selectedInstanceId={selectedInstanceId}
        />
      </Stack>
    </PageSection>
  );
};
